import { Box, Grid } from '@mui/material';
import {
  AdminDatabaseHealth,
  DatabaseGrowthDataPoint,
  SlowQuery,
  TableSize,
  QueryDurationDataPoint,
} from '../../../types/admin';
import RefreshButton from './shared/RefreshButton';
import AdminMetricCard from '../shared/AdminMetricCard';
import DatabaseGrowthChart from './database/DatabaseGrowthChart';
import SlowQueryTable from './database/SlowQueryTable';
import ConnectionPoolGauge from './database/ConnectionPoolGauge';
import { formatNumber } from '../../../utils/formatters';

interface DatabaseHealthTabProps {
  databaseHealth: AdminDatabaseHealth;
  growthHistory: DatabaseGrowthDataPoint[];
  slowQueries: SlowQuery[];
  tableSizes: TableSize[];
  queryDurationDistribution: QueryDurationDataPoint[];
  loading?: boolean;
  lastUpdated: string;
  onRefresh: () => void;
}

export default function DatabaseHealthTab({
  databaseHealth,
  growthHistory,
  slowQueries,
  loading,
  lastUpdated,
  onRefresh,
}: DatabaseHealthTabProps) {
  return (
    <Box>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <RefreshButton onRefresh={onRefresh} lastUpdated={lastUpdated} loading={loading} />
      </Box>

      {/* Database Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Database Size"
            value={`${databaseHealth.database_size_mb} MB`}
            subtitle={`Growing at ${databaseHealth.growth_rate_mb_per_day} MB/day`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Avg Query Time"
            value={`${databaseHealth.avg_query_time_ms}ms`}
            subtitle={`${databaseHealth.slow_query_count} slow queries`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Transaction Rate"
            value={`${databaseHealth.transaction_rate}/sec`}
            subtitle="Current throughput"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Query Volume"
            value={formatNumber(
              databaseHealth.query_volume.select +
                databaseHealth.query_volume.insert +
                databaseHealth.query_volume.update +
                databaseHealth.query_volume.delete
            )}
            subtitle="Total queries"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Connection Pool */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <ConnectionPoolGauge
          activeConnections={databaseHealth.active_connections}
          poolSize={databaseHealth.connection_pool_size}
          utilization={databaseHealth.connection_pool_utilization}
        />
      </Box>

      {/* Charts and Tables */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <DatabaseGrowthChart data={growthHistory} loading={loading} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <SlowQueryTable queries={slowQueries} loading={loading} />
        </Grid>
      </Grid>
    </Box>
  );
}
